import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { axios } from "@via/api/axios";
import {
  type ConfigurationsResponse,
  type UserConfigurationsResponse,
} from "common";

/**
 * Fetch application configurations.
 *
 * A 200 is not proof the body is the configuration. A proxy's own sign-in page, or the SPA's
 * index.html fallback, also answers with 200, and axios hands that back as a string. Throwing here
 * turns such a response into a failed query, so every caller keeps the last good configuration
 * instead of receiving the string.
 */
export const fetchConfigurations =
  async (): Promise<ConfigurationsResponse> => {
    try {
      const res = await axios.get("/api/configurations/v1");
      if (!Array.isArray(res.data?.enabledAuthSystems)) {
        throw new Error(
          "The configuration response is not the configuration object"
        );
      }
      return res.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const useConfigurations = (): UseQueryResult<
  ConfigurationsResponse,
  Error
> => {
  return useQuery<ConfigurationsResponse, Error>({
    queryKey: ["/api/configurations/v1"],
    queryFn: fetchConfigurations,
    retry: false,
  });
};

export const useChangelog = () => {
  return useQuery({
    queryKey: ["changelog"],
    queryFn: async () => {
      try {
        const response = await axios.get(`/api/configurations/v1/changelog`);
        return response.data;
      } catch (error) {
        console.error(error);
        throw new Error("Failed to fetch changelog");
      }
    },
  });
};

export const useBreakingChanges = (enabled: boolean) => {
  return useQuery({
    queryKey: ["breaking-changes"],
    queryFn: async () => {
      try {
        const response = await axios.get(
          `/api/configurations/v1/breaking-changes`
        );
        return response.data;
      } catch (error) {
        console.error(error);
        throw new Error("Failed to fetch breaking changes");
      }
    },
    enabled,
  });
};

export const fetchUserConfigurations =
  async (): Promise<UserConfigurationsResponse> => {
    try {
      const res = await axios.get("/api/configurations/v1/user");
      return res.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const useUserConfigurations = (): UseQueryResult<
  UserConfigurationsResponse,
  Error
> => {
  return useQuery<UserConfigurationsResponse, Error>({
    queryKey: ["/api/configurations/v1/user"],
    queryFn: fetchUserConfigurations,
    retry: false,
  });
};
